<template>
  <InfoModal />
  <div>
    <q-breadcrumbs class="text-brown navigation-breadcrumbs">
      <template v-slot:separator>
        <q-icon size="1.5em" name="chevron_right"></q-icon>
      </template>

      <q-breadcrumbs-el :label="t('profile.profile')"></q-breadcrumbs-el>
      <q-breadcrumbs-el
        :label="t('navbar.account_management')"
      ></q-breadcrumbs-el>

      <q-breadcrumbs-el
        :label="t('account_management.users')"
      ></q-breadcrumbs-el>
    </q-breadcrumbs>
    <div class="text-h4 text-left main-caption">
      {{ t("navbar.account_management") }}
    </div>
    <p class="heading2 q-mb-xl">
      {{ t("account_management.view_account_information_of_organization") }}
    </p>

    <div style="display: flex; flex-direction: column">
      <div class="search-btn-container">
        <DxButton
          id="whiteIconBtn"
          :text="t('account_management.filter')"
          icon="filter"
          class="orange-btn-filled"
          style="margin-right: 5px"
          @click="enableFilterChooser"
          :rtlEnabled="true"
          v-if="!addNewUserDrawerVisible && !editUserDrawerVisible"
        />
      </div>

      <div class="filter-form" v-if="filterChooserVisible">
        <div class="button-row">
          <DxButton icon="remove" @click="disableFilterChooser" />
        </div>
        <div class="dx-fieldset">
          <div class="dx-field">
            <div class="dx-field-label">
              {{ t("account_management.user_name") }}
            </div>
            <div class="dx-field-value">
              <DxTextBox
                v-model:value="userNameSearchTerm"
                :placeholder="t('account_management.user_name')"
              >
              </DxTextBox>
            </div>
          </div>
          <div class="dx-field" v-if="customerHasSas">
            <div class="dx-field-label">
              {{ t("account_management.filter_products") }}
            </div>
            <div class="dx-field-value">
              <DxRadioGroup
                :items="radioItemsProducts"
                v-model:value="displayContentUsers"
                layout="horizontal"
                displayExpr="text"
                valueExpr="value"
              >
              </DxRadioGroup>
            </div>
          </div>

          <div class="dx-field">
            <div class="dx-field-label">
              {{ t("account_management.active") }}
            </div>
            <div class="dx-field-value">
              <DxRadioGroup
                :items="isActiveOptions"
                v-model:value="displayOnlyActiveUsersFilter"
                layout="horizontal"
                displayExpr="text"
                valueExpr="value"
              >
              </DxRadioGroup>
            </div>
          </div>
          <div class="button-row">
            <DxButton
              id="whiteIconBtn"
              :text="t('search_schemes.search')"
              icon="find"
              iconPosition="right"
              class="orange-btn-filled"
              style="margin-top: 12px"
              @click="getFilteredUsers()"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="users-grid">
      <DxDrawer
        class="resizable"
        opened-state-mode="shrink"
        position="right"
        reveal-mode="slide"
        v-model:opened="drawerVisible"
        shading="true"
        id="usersGrid"
        template="drawerTemplate"
      >
        <template #drawerTemplate>
          <div v-if="addNewUserDrawerVisible && !editUserDrawerVisible">
            <AddUserDrawer />
          </div>
          <div v-else-if="editUserDrawerVisible && !addNewUserDrawerVisible">
            <EditUserDrawer :onSave="refreshGrid" />
          </div>
        </template>
        <div class="content-block">
          <div class="dx-card">
            <DxDataGrid
              ref="usersManagementGridRef"
              :remote-operations="true"
              :no-data-text="t('common.no_data_message')"
              :data-source="users"
              :show-row-lines="true"
              :allow-column-resizing="true"
              key-expr="id"
              @exporting="exportExcel"
              :column-auto-width="true"
              :master-detail="{ enabled: isMobile, autoExpandAll: false }"
            >
              <DxExport :enabled="true" />
              <DxStateStoring
                :enabled="true"
                type="localStorage"
                savingTimeout="500"
                storage-key="usersManagementGridStorage"
              />
              <DxToolbar style="width: 200px" class="grid-toolbar">
                <DxItem location="before" template="gridHeadingTemplate" />
                <DxItem location="after" template="gridHeadingButtonTemplate" />
                <DxItem
                  name="columnChooserButton"
                  locate-in-menu="auto"
                  location="after"
                />
                <DxItem :location="'before'" :name="'exportButton'" />
              </DxToolbar>
              <DxLoadPanel :enabled="true" />
              <DxPaging :page-size="20" />
              <DxPager
                :visible="true"
                :display-mode="compact"
                :show-navigation-buttons="true"
                :show-info="true"
                :show-page-size-selector="true"
                :allowed-page-sizes="[10, 20, 50, 'all']"
                :info-text="pageInfoText"
              />
              <DxColumnChooser
                :enabled="!isMobile"
                mode="select"
                height="370"
                width="400"
                :title="t('common.column_chooser')"
              >
                <DxColumnChooserSelection
                  allow-select-all="true"
                  select-by-click="true"
                />
                <DxPosition
                  my="right top"
                  at="right bottom"
                  of=".dx-datagrid-column-chooser-button"
                />
              </DxColumnChooser>
              <DxColumn
                data-field="fullName"
                :caption="t('account_management.full_name')"
              />
              <DxColumn
                v-if="!isMobile"
                data-field="email"
                :allow-editing="false"
                :caption="t('account_management.user_name')"
                :visible="true"
              />
              <DxColumn
                v-if="customerHasSas && !isMobile"
                :allow-filtering="false"
                :allow-sorting="false"
                data-field="sasRoles"
                data-type="object"
                :width="200"
                :caption="t('account_management.sas_roles')"
                cell-template="cell"
                :visible="true"
              />
              <template #cell="{ data }">
                <Badge
                  v-if="
                    data.data &&
                    data.data.sasRoles &&
                    data.data.sasRoles.length > 0
                  "
                  :data="data.data.sasRoles"
                  displayExpr="name"
                  :colorClass="data.data.hasSas ? 'badge-green' : 'badge-red'"
                />
              </template>
              <DxColumn
                v-if="!isMobile"
                :allow-filtering="false"
                :allow-sorting="false"
                data-field="contentRoles"
                data-type="object"
                :width="200"
                :caption="t('account_management.content_roles')"
                cell-template="cellContentRoles"
                :visible="true"
              />
              <template #cellContentRoles="{ data }">
                <Badge
                  v-if="
                    data.data &&
                    data.data.contentRoles &&
                    data.data.contentRoles.length > 0
                  "
                  :data="data.data.contentRoles"
                  displayExpr="name"
                  :colorClass="
                    !data.data.hasPasswordToken
                      ? 'badge-orange'
                      : data.data.hasContent
                      ? 'badge-green'
                      : 'badge-red'
                  "
                />
              </template>
              <DxColumn
                v-if="customerHasSas && !isMobile"
                :visible="false"
                data-field="receivingSasTaskEmails"
                :allow-filtering="false"
                :caption="t('account_management.receives_sas_task_emails')"
                cssClass="no-padding"
                :showInColumnChooser="true"
              />
              <DxColumn
                v-if="!isMobile"
                data-field="salutation"
                :caption="t('account_management.salutation')"
                :calculate-cell-value="(e) => getSalutationText(e)"
                :showInColumnChooser="true"
                :allow-filtering="false"
                :allow-sorting="false"
                :visible="false"
              />
              <DxColumn
                v-if="!isMobile"
                data-field="department"
                :caption="t('account_management.department')"
                :visible="true"
              />
              <DxColumn
                type="buttons"
                :caption="t('common.action')"
                :showInColumnChooser="false"
              >
                <DxGridButton
                  :visible="(e) => e.row.data.isUserAnonymous === false"
                  name="edit"
                  icon="las la-pencil-alt"
                  :hint="t('account_management.edit')"
                  @click="enableEditUser"
                />
                <DxGridButton
                  :visible="(e) => e.row.data.hasSas || e.row.data.hasContent"
                  name="deactivate"
                  :hint="t('account_management.deactivate')"
                  icon="las la-ban"
                  @click="triggerDeactivationModal"
                />
                <DxGridButton
                  :visible="
                    (e) =>
                      !e.row.data.hasSas &&
                      !e.row.data.hasContent &&
                      !e.row.data.isUserAnonymous
                  "
                  name="reactivate"
                  :hint="t('account_management.reactivate')"
                  icon="las la-check"
                  @click="triggerReactivationModal"
                />
                <DxGridButton
                  :visible="
                    (e) =>
                      e.row.data.hasSas &&
                      !e.row.data.isUserAnonymous &&
                      customerHasSas &&
                      customerHas2FA
                  "
                  name="resetMfa"
                  :hint="t('account_management.resetMfa')"
                  icon="las la-qrcode"
                  @click="resetMfa"
                />
                <DxGridButton
                  :visible="
                    (e) =>
                      !e.row.data.isUserAnonymous &&
                      !e.row.data.hasSas &&
                      !e.row.data.hasContent &&
                      isSuperAdmin
                  "
                  name="anonymize"
                  :hint="t('account_management.anonymize')"
                  icon="las la-2x la-user-slash la-fw"
                  @click="triggerAnonymizeModal"
                />
                <DxGridButton
                  :visible="(e) => e.row.data.isUserAnonymous === false"
                  name="sendPasswordSetup"
                  :hint="t('account_management.recover_password')"
                  icon="las la-2x la-mail-bulk la-fw"
                  @click="sendPasswordSetupMail"
                />
              </DxColumn>
              <DxMasterDetail
                :enabled="true"
                template="masterDetailTemplate"
              /><template #masterDetailTemplate="{ data }">
                <div class="detail-content">
                  <div v-if="data.data.email">
                    <strong>{{ t("account_management.user_name") }}: </strong>
                    {{ data.data.email }}
                  </div>
                  <div v-if="data.data.salutation">
                    <strong>{{ t("account_management.salutation") }}:</strong>
                    {{ getSalutationText(data.data) }}
                  </div>
                  <div v-if="data.data.department">
                    <strong>{{ t("account_management.department") }}:</strong>
                    {{ data.data.department }}
                  </div>
                </div>
              </template>
              <template #gridHeadingTemplate>
                <h1 class="grid-heading">
                  {{ t("account_management.users") }}
                </h1>
              </template>
              <template #gridHeadingButtonTemplate>
                <DxButton
                  id="whiteIconBtn"
                  class="orange-btn-filled bold-font"
                  :text="t('account_management.add_new_user')"
                  icon="plus"
                  iconPosition="right"
                  @click="enableAddUser()"
                  :aria-label="t('account_management.add_new_user')"
                >
                </DxButton>
              </template>
            </DxDataGrid>
          </div>
        </div>
      </DxDrawer>
    </div>
  </div>

  <DxPopup
    v-model:visible="confirmDeactivation"
    :width="550"
    :height="217"
    :show-title="true"
    :drag-enabled="false"
    :hide-on-outside-click="false"
    :wrapper-attr="popupAttributes"
  >
    <div class="deactivation-popup-content">
      <div class="row large-text" style="font-family: ProximaNova-Regular">
        <i class="las la-2x la-ban la-fw"></i>
        <span>{{ t("account_management.deactivate_user_question") }}</span>
        <span v-if="customerHasSas">{{
          t("account_management.deactivate_user_sas_info")
        }}</span>
      </div>
      <div
        class="row"
        style="display: flex; justify-content: space-evenly; margin-top: 1rem"
      >
        <DxButton
          class="orange-btn-filled"
          :text="t('common.cancel')"
          :aria-label="t('common.cancel')"
          width="120px"
          @click="confirmDeactivation = false"
        />
        <DxButton
          class="orange-btn"
          :text="t('account_management.deactivate')"
          :aria-label="t('account_management.deactivate')"
          width="120px"
          @click="deactivateUser(selectedUserId)"
        />
      </div>
    </div>
  </DxPopup>
  <DxPopup
    v-model:visible="confirmReactivation"
    :width="550"
    :height="217"
    :show-title="true"
    :drag-enabled="false"
    :hide-on-outside-click="false"
    :wrapper-attr="popupAttributes"
  >
    <div class="deactivation-popup-content">
      <div class="row large-text" style="font-family: ProximaNova-Regular">
        <i class="las la-2x la-sync la-fw"></i>
        <span class="q-ml-sm">{{
          t("account_management.activate_user_question")
        }}</span>
      </div>
      <div
        class="row"
        style="display: flex; justify-content: space-evenly; margin-top: 1rem"
      >
        <DxButton
          class="orange-btn-filled"
          :text="t('common.cancel')"
          :aria-label="t('common.cancel')"
          width="120px"
          @click="confirmReactivation = false"
        />
        <DxButton
          class="orange-btn"
          :text="t('account_management.reactivate')"
          :aria-label="t('account_management.reactivate')"
          width="120px"
          @click="reactivateUser(selectedUserId)"
        />
      </div>
    </div>
  </DxPopup>
  <DxPopup
    v-model:visible="showAnonymizePopup"
    :width="550"
    :height="217"
    :show-title="true"
    :drag-enabled="false"
    :hide-on-outside-click="false"
    :wrapper-attr="popupAttributes"
    :title="t('account_management.anonymize_users_header')"
  >
    <div class="deactivation-popup-content">
      <div class="row large-text" style="font-family: ProximaNova-Regular">
        <span class="q-ml-sm">
          {{ t("account_management.anonymize_users_warning_message") }}
        </span>
      </div>
      <div
        class="row"
        style="display: flex; justify-content: space-evenly; margin-top: 1rem"
      >
        <DxButton
          class="orange-btn-filled"
          :text="t('common.cancel')"
          :aria-label="t('common.cancel')"
          width="140px"
          @click="showAnonymizePopup = false"
        />
        <DxButton
          class="orange-btn"
          :text="t('account_management.anonymize')"
          :aria-label="t('account_management.anonymize')"
          width="140px"
          @click="anonymizeUser(selectedUserId)"
        />
      </div>
    </div>
  </DxPopup>
</template>
<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import {
  DxDataGrid,
  DxStateStoring,
  DxToolbar,
  DxItem,
  DxLoadPanel,
  DxPager,
  DxPaging,
  DxColumnChooser,
  DxColumnChooserSelection,
  DxPosition,
  DxColumn,
  DxButton as DxGridButton,
  DxExport,
  DxMasterDetail,
} from "devextreme-vue/data-grid";
import { DxDrawer } from "devextreme-vue/drawer";
import { DxButton } from "devextreme-vue/button";
import { DxPopup } from "devextreme-vue/popup";
import { DxRadioGroup } from "devextreme-vue/radio-group";
import { DxTextBox } from "devextreme-vue/text-box";
import AddUserDrawer from "./AddUserDrawer.vue";
import EditUserDrawer from "./EditUserDrawer.vue";
import Badge from "../global-components/GridBadge.vue";
import InfoModal from "../global-components/InfoModal.vue";
import { remoteDataSource } from "../../store/modules/custom.store";
import { useI18n } from "vue-i18n";
import store from "../../store";
import UserService from "../../services/userprofile.service";

const { t, locale } = useI18n();
const usersManagementGridRef = ref(null);
const selectedUserId = ref(null);
const confirmDeactivation = ref(false);
const confirmReactivation = ref(false);
const showAnonymizePopup = ref(false);
const userNameSearchTerm = ref("");
const displayOnlyActiveUsersFilter = ref(null);
const customerHasSas = computed(() => store.state.auth.authUser.customerHasSas);
const customerHas2FA = computed(() => store.state.auth.authUser.customerHas2FA);

const displayContentUsers = ref(customerHasSas.value ? null : true);

const additionalFilter = ref({
  username: userNameSearchTerm,
  displayContentUsers: displayContentUsers, // null: both, true: content only, false: SAS only
  displayOnlyActiveUsers: displayOnlyActiveUsersFilter,
});

const users = ref(remoteDataSource(additionalFilter.value));

const isMobile = ref(false);

onMounted(async () => {
  checkScreenSize();
  window.addEventListener("resize", checkScreenSize);
  await UserService.getCustomerByUser(locale);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkScreenSize);
});

const refreshGrid = () => {
  usersManagementGridRef.value.instance.refresh();
};
const drawerVisible = computed(
  () => addNewUserDrawerVisible.value || editUserDrawerVisible.value
);
const addNewUserDrawerVisible = computed(() => store.state.user.showAddUser);
const editUserDrawerVisible = computed(
  () => store.state.user.showEditUserDrawer
);
const isSuperAdmin = computed({
  get: () => store.state.auth.authUser.isSuperAdmin,
});

const salutations = computed({
  get: () => store.state.user.salutationsList,
});

const getSalutationText = (rowData) => {
  let salutation = salutations.value.find((s) => s.id == rowData.salutation);
  return salutation ? salutation.description : "";
};

const pageInfoText = computed(
  () =>
    ` ${t("common.page")} {0} ${t("common.of")} {1} ({2} ${t("common.items")})`
);

const enableAddUser = () => {
  store.commit("user/enableAddUser");
};

const enableEditUser = (e) => {
  store.commit("user/enableEditUser", e.row.data);
};

watch(
  () => store.state.user.refreshUsersGrid,
  () => {
    if (usersManagementGridRef.value && usersManagementGridRef.value.instance) {
      refreshGrid();
    }
  }
);

const sendPasswordSetupMail = async (e) => {
  await UserService.sendPasswordSetupMail(e.row.data.id, locale).then(
    (response) => {
      if (response.status === 200) {
        store.commit("infoModal/openInfoModal");
        store.commit(
          "infoModal/setInfoModalText",
          t("account_management.email_sent")
        );
        refreshGrid();
      }
    }
  );
};

const triggerDeactivationModal = (e) => {
  selectedUserId.value = e.row.data.id;
  confirmDeactivation.value = true;
};
const triggerReactivationModal = (e) => {
  selectedUserId.value = e.row.data.id;
  confirmReactivation.value = true;
};
const triggerAnonymizeModal = (e) => {
  showAnonymizePopup.value = true;
  selectedUserId.value = e.row.data.id;
};

const deactivateUser = async (selectedUserId) => {
  await UserService.deactivateUser(selectedUserId, locale);
  confirmDeactivation.value = false;
  refreshGrid();
};
const reactivateUser = async (selectedUserId) => {
  await UserService.reactivateUser(selectedUserId, locale);
  confirmReactivation.value = false;
  refreshGrid();
};
const anonymizeUser = async (userId) => {
  await UserService.anonymizeUser(userId, locale);
  showAnonymizePopup.value = false;
  refreshGrid();
};
const resetMfa = async (e) => {
  await UserService.resetMfaForUser(e.row.data.id).then((response) => {
    if (response.status === 200) {
      store.commit("infoModal/openInfoModal");
      store.commit(
        "infoModal/setInfoModalText",
        t("account_management.mfa_successfully_reset")
      );
    }
  });
};

const filterChooserVisible = computed(
  () => store.state.user.filterChooserVisible
);
const enableFilterChooser = () => store.commit("user/enableFilterChooser");
const disableFilterChooser = () => store.commit("user/disableFilterChooser");

const radioItemsProducts = computed(() => [
  { text: "Content", value: true },
  { text: "SAS", value: false },
  { text: t("account_management.filter_both"), value: null },
]);

const isActiveOptions = computed(() => [
  { text: t("common.yes"), value: true },
  { text: t("common.no"), value: false },
  { text: t("account_management.filter_both"), value: null },
]);

const getFilteredUsers = () => {
  usersManagementGridRef.value.instance.refresh();
  disableFilterChooser();
};

const exportExcel = async () => {
  await UserService.exportUsers()
    .then((response) => {
      const fileURL = window.URL.createObjectURL(
        new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        })
      );
      const a = document.createElement("a");
      a.style.display = "none";
      document.body.appendChild(a);
      a.href = fileURL;
      a.download = "Gebruikers.xlsx";
      a.click();
      window.URL.revokeObjectURL(fileURL);
      document.body.removeChild(a);
    })
    .catch((error) => {
      console.error("Error downloading the file:", error);
    });
};

const checkScreenSize = () => {
  if (typeof window !== "undefined") {
    isMobile.value = window.innerWidth < 768;
  }
};
</script>
